<script setup lang="ts">
import { Database, Gift, Pencil, Trash2, TriangleAlert, X } from 'lucide-vue-next'
import { useContestRewards } from '../composables/useContestRewards'

import { formatProbability, isRewardPlaceholder, roundToDecimals, validateProbability } from '../utils'
import ContestLibraryRewardModal from './ContestLibraryRewardModal.vue'
import ContestLostRewardModal from './ContestLostRewardModal.vue'
import ContestPointsModal from './ContestPointsModal.vue'
import RewardSelectorModal from './RewardSelectorModal.vue'

import { getRewardTypeIcon, getRewardTypeLabel } from '@/components/rewards/utils/rewardHelpers'

import type { Contest, LoyaltyReward } from '@/types/types'

const { t } = useI18n()
const { formatNumber } = useNumber()

interface Props {
  getContest: Contest
  isEditMode: boolean
  autoOpenRewardSelection?: boolean
  libraryRewards?: LoyaltyReward[]
  isLoadingLibraryRewards?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isEditMode: false,
  autoOpenRewardSelection: false,
  libraryRewards: () => [],
  isLoadingLibraryRewards: false
})

const emit = defineEmits<{
  (e: 'update:modelValue', contest: Contest): void
  (e: 'change', contest: Contest): void
  (e: 'error', error: string): void
  (e: 'reward-selection-shown'): void
  (e: 'refresh-library-rewards'): void
}>()

const {
  contest,
  openedRewardModalId,
  openedModalType,
  selectedReward,
  totalProbability,
  remainingProbability,
  hasProbabilityError,
  tableRows,
  updateRewardPercentage,
  deleteReward,
  editReward,
  addNewReward,
  addLibraryRewards,
  librarySelectionLimit,
  closeRewardModal,
  handleRewardUpdate
} = useContestRewards(props.getContest, emit)

const isRewardSelectionOpen = ref(false)
const isLibraryRewardCreationOpen = ref(false)
const preselectedRewardIds = ref<number[]>([])

const rewardSlots = computed(() =>
  contest.value.contestGameRewards
    .filter(isRewardPlaceholder)
    .map((reward) => ({ name: reward.name, probability: reward.probability }))
)

const closeRewardSelection = () => {
  isRewardSelectionOpen.value = false
  preselectedRewardIds.value = []
}

const openLibraryRewardCreation = (selectedIds: number[]) => {
  preselectedRewardIds.value = selectedIds
  isRewardSelectionOpen.value = false
  isLibraryRewardCreationOpen.value = true
}

const backToRewardSelection = () => {
  isLibraryRewardCreationOpen.value = false
  isRewardSelectionOpen.value = true
}

const handleLibraryRewardCreated = (rewardId: number) => {
  preselectedRewardIds.value = [...preselectedRewardIds.value, rewardId]
  emit('refresh-library-rewards')
  backToRewardSelection()
}

const handleLibraryRewardsSelected = (rewardIds: number[]) => {
  closeRewardSelection()
  addLibraryRewards(
    rewardIds.map((id) => props.libraryRewards.find((reward) => reward.id === id)).filter((reward) => !!reward)
  )
}

onMounted(() => {
  if (!props.autoOpenRewardSelection) return

  emit('reward-selection-shown')
  if (librarySelectionLimit.value > 0) isRewardSelectionOpen.value = true
})

const tableColumns = computed(() => ({
  image: {
    label: '',
    headerClass: 'w-16!'
  },
  name: {
    label: t('pages.contests.table.columns.name'),
    headerClass: 'w-64!'
  },
  type: {
    label: t('pages.contests.table.columns.type'),
    headerClass: 'w-40!'
  },
  points: {
    label: t('pages.contests.table.columns.points'),
    headerClass: 'w-32!',
    numeric: true
  },
  probability: {
    label: t('pages.contests.table.columns.probability'),
    headerClass: 'w-32!'
  },
  actions: {
    label: t('pages.contests.table.columns.actions'),
    headerClass: 'w-28!',
    align: 'right' as const
  }
}))

const handleDeleteReward = (index: number) => {
  deleteReward(index)
}

const handleUpdateRewardPercentage = (index: number, event: Event) => {
  const value = (event.target as HTMLInputElement).value
  const currentGameReward = contest.value.contestGameRewards[index]

  if (
    !((value.includes(',') || value.includes('.')) && value.endsWith('0')) &&
    Number(currentGameReward?.probability) !== Number(value)
  ) {
    updateRewardPercentage(index, Number(value))
  }
}

const handleRowClicked = (row: any) => {
  editReward(row.id)
}
</script>

<template>
  <div>
    <div class="flex flex-1 flex-col">
      <div>
        <AppTable
          :columns="tableColumns"
          :rows="tableRows"
          row-classes="cursor-pointer"
          @row-clicked="handleRowClicked"
        >
          <template #empty-state>
            <div class="mx-auto h-10 w-10 text-muted-foreground">
              <BaseIcon name="HeroGiftIconOutline" class="h-10 w-10" />
            </div>
            <h3 class="mt-2 text-sm font-medium text-foreground">{{ t('pages.contests.empty.title') }}</h3>
            <p class="mt-1 text-sm text-muted-foreground">{{ t('pages.contests.empty.description') }}</p>
          </template>

          <template #title>
            <div class="flex items-center justify-between">
              <CardTitle>{{ t('pages.contests.table.title') }}</CardTitle>
              <div class="flex gap-3">
                <Button variant="outline" @click="addNewReward('gift')">
                  <Gift />
                  {{ t('pages.contests.buttons.reward') }}
                </Button>
                <Button variant="outline" @click="addNewReward('points')">
                  <Database />
                  {{ t('pages.contests.buttons.points') }}
                </Button>
                <Button variant="outline" @click="addNewReward('lost')">
                  <X />
                  {{ t('pages.contests.buttons.lost') }}
                </Button>
              </div>
            </div>
          </template>

          <template #th-actions>
            <div></div>
          </template>

          <template #td-image="{ data }">
            <div class="relative h-10 w-16 min-w-16 overflow-hidden rounded-lg">
              <img
                v-if="data?.pictureUrl"
                :src="data.pictureUrl"
                :alt="data.name || t('pages.contests.image_alt')"
                class="h-full w-full object-cover"
              />

              <div v-else class="flex h-full w-full items-center justify-center bg-muted">
                <BaseIcon
                  :name="getRewardTypeIcon({ slug: data?.rewardTypeSlug, name: data?.name })"
                  class="h-5 w-5 text-muted-foreground"
                />
              </div>
            </div>
          </template>

          <template #td-type="{ data }">
            <BaseChip :text="getRewardTypeLabel({ slug: data?.rewardTypeSlug, name: data?.name }, t)" color="gray" />
          </template>

          <template #td-name="{ data }">
            <div
              v-if="data?.rewardTypeSlug === 'free_product' && !data?.rewardId"
              class="text-sm font-medium text-yellow-500"
            >
              {{ t('pages.contests.contestsCreatePage.prices.selectReward') }}
            </div>
            <div v-else class="text-sm font-medium text-foreground">
              {{ data?.name || '-' }}
            </div>
          </template>

          <template #td-points="{ data }">{{ data?.points ? formatNumber(data.points) : '-' }}</template>

          <template #td-probability="{ data }">
            <div class="space-y-1">
              <InputGroup class="w-70" @click.stop>
                <InputGroupInput
                  :id="`gameReward.${contest.contestGameRewards.indexOf(data)}.probability`"
                  :model-value="data.probability"
                  type="number"
                  min="0"
                  max="100"
                  step="0.0001"
                  :aria-invalid="!validateProbability(data.probability)"
                  @input="handleUpdateRewardPercentage(contest.contestGameRewards.indexOf(data), $event)"
                />
                <InputGroupAddon align="inline-end">
                  <InputGroupText>{{ t('pages.contests.probability.unit') }}</InputGroupText>
                </InputGroupAddon>
              </InputGroup>
              <div v-if="!validateProbability(data.probability)" class="text-xs text-red-600" @click.stop>
                {{ t('pages.contests.probability.error.range') }}
              </div>
            </div>
          </template>

          <template #td-actions="{ data }">
            <div class="flex items-center justify-end gap-2">
              <Button
                variant="outline"
                size="icon"
                :title="t('pages.contests.actions.edit')"
                @click.stop="editReward(contest.contestGameRewards.indexOf(data))"
              >
                <Pencil />
              </Button>
              <Button
                variant="outline-destructive"
                size="icon"
                :title="t('pages.contests.actions.delete')"
                @click.stop="handleDeleteReward(contest.contestGameRewards.indexOf(data))"
              >
                <Trash2 />
              </Button>
            </div>
          </template>

          <template #footer>
            <tr>
              <td v-if="hasProbabilityError" colspan="4" class="">
                <div class="px-4 py-3 flex-row flex items-center text-sm text-red-600">
                  <TriangleAlert class="h-4 w-4 text-red-500 mr-2" />
                  <span class="font-medium">{{ t('pages.contests.total.tooMuchError') }}</span>
                </div>
              </td>

              <td v-if="totalProbability < 100" colspan="4" class="">
                <div class="px-4 py-3 flex-row flex items-center text-sm text-yellow-500">
                  <TriangleAlert class="h-4 w-4 text-yellow-500 mr-2" />
                  <span class="font-medium">{{
                    t('pages.contests.total.remainingWarning', { percentage: roundToDecimals(remainingProbability) })
                  }}</span>
                </div>
              </td>

              <td v-if="totalProbability === 100" colspan="4">
                <div class="px-4 py-3 flex-row flex items-center text-sm text-green-500">
                  <BaseIcon name="HeroCheckCircleIconSolid" class="h-4 w-4 text-green-500 mr-2" />

                  <span class="font-medium">
                    {{ t('pages.contests.total.valid') }}
                  </span>
                </div>
              </td>

              <td colspan="1" class="text-sm text-foreground-secondary px-4 py-3">
                <span class="font-medium"
                  >{{ t('pages.contests.total.label') }} :
                  <span
                    :class="{
                      'text-red-600': hasProbabilityError,
                      'text-yellow-500': totalProbability < 100,
                      'text-green-500': totalProbability === 100
                    }"
                    >{{ formatProbability(totalProbability) }}</span
                  ></span
                >
              </td>

              <td colspan="1"></td>
            </tr>
          </template>
        </AppTable>
      </div>
    </div>

    <ContestPointsModal
      v-if="openedModalType === 'points' && openedRewardModalId !== null"
      :reward="selectedReward"
      :message-template="contest.messageTemplate"
      @close="closeRewardModal"
      @update:reward="handleRewardUpdate"
    />

    <ContestLostRewardModal
      v-if="openedModalType === 'lost' && openedRewardModalId !== null"
      :reward="selectedReward"
      :message-template="contest.messageTemplate"
      @close="closeRewardModal"
      @update:reward="handleRewardUpdate"
    />

    <RewardSelectionModal
      :is-open="isRewardSelectionOpen"
      :rewards="libraryRewards"
      :is-loading="isLoadingLibraryRewards"
      multiple
      :max-selected="librarySelectionLimit"
      :slots="rewardSlots"
      :preselected-ids="preselectedRewardIds"
      @close-modal="closeRewardSelection"
      @create-reward="openLibraryRewardCreation"
      @select-rewards="handleLibraryRewardsSelected"
    />

    <ContestLibraryRewardModal
      v-if="isLibraryRewardCreationOpen"
      @close="backToRewardSelection"
      @saved="handleLibraryRewardCreated"
    />

    <RewardSelectorModal
      v-if="openedModalType === 'gift' && selectedReward"
      :reward="selectedReward"
      :message-template="contest.messageTemplate"
      :channel-type="contest.messageTemplate.messageChannelId === 1 ? 'sms' : 'mail'"
      @close="closeRewardModal"
      @update:reward="handleRewardUpdate"
    />
  </div>
</template>

<style scoped>
table {
  border-collapse: separate;
  border-spacing: 0;
}

th {
  position: sticky;
  top: 0;
  z-index: 10;
}
</style>
